import { JourneyFrame, Note } from './JourneyChrome'
import { type LocalMaterial } from './MaterialEntry'
import { Original } from './ReadMaterial'
import { PANEL, PRIMARY, SECONDARY } from './ui'

/**
 * Concept-first Mathematics (D-21): after the learner confirms the text, the
 * app names the topic it recognised and asks before teaching it. Recognition
 * is a deterministic check for fraction additions/subtractions in the
 * confirmed text (`fraction-expressions.ts`), not an understanding of the
 * whole page, and the screen says so. When the topic is not recognised, or
 * the learner says it is wrong, the fallback explains why and lets the learner
 * look at the original. No practice is made and nothing counts as evidence.
 */

export const FRACTION_TOPIC_LABEL = 'Cộng, trừ phân số khác mẫu số'

export function TopicConfirm({
  busy,
  busyLabel,
  error,
  onConfirm,
  onReject,
  onHome,
}: {
  busy: boolean
  /** What the confirm button says while busy (default: preparing the lesson). */
  busyLabel?: string | undefined
  error: string | null
  onConfirm: () => void
  onReject: () => void
  onHome: () => void
}): React.JSX.Element {
  return (
    <JourneyFrame
      step="Chủ đề"
      title="Có phải chủ đề này không?"
      onBack={null}
      onHome={onHome}
    >
      <section aria-labelledby="topic-found" className={PANEL}>
        <h2 id="topic-found" className="text-lg font-semibold">
          Ứng dụng thấy tài liệu của con có bài về:
        </h2>
        <p className="text-2xl font-semibold">{FRACTION_TOPIC_LABEL}</p>
        <p className="text-base text-slate-700 dark:text-slate-300">
          Ứng dụng nhận ra chủ đề từ các phép tính phân số con đã xác nhận, chứ không hiểu
          hết cả trang. Nếu không đúng, con bấm “Không phải chủ đề này”.
        </p>
      </section>
      <Note tone="info">
        Ứng dụng sẽ giải thích cách làm và cho con 2 bài luyện khác. Ứng dụng không giải
        bài trong tài liệu của con.
      </Note>
      {error !== null && (
        <p
          role="alert"
          className="rounded-xl bg-rose-50 px-3 py-2 text-lg text-rose-900 dark:bg-rose-950/50 dark:text-rose-100"
        >
          {error}
        </p>
      )}
      <div className="flex flex-col gap-3 md:flex-row">
        <button type="button" className={PRIMARY} disabled={busy} onClick={onConfirm}>
          {busy ? (busyLabel ?? 'Đang chuẩn bị bài học…') : 'Đúng, học chủ đề này'}
        </button>
        <button type="button" className={SECONDARY} disabled={busy} onClick={onReject}>
          Không phải chủ đề này
        </button>
      </div>
    </JourneyFrame>
  )
}

export type FallbackReason = 'not_recognised' | 'rejected' | 'no_practice'

const FALLBACK_TEXT: Readonly<Record<FallbackReason, string>> = {
  not_recognised:
    'Ứng dụng chưa nhận ra chắc chắn chủ đề của tài liệu này. Hiện ứng dụng chỉ học được phần cộng, trừ hai phân số khác mẫu số, viết dạng như 3/4 + 1/8.',
  rejected:
    'Con nói đây không phải chủ đề cộng, trừ phân số. Ứng dụng chưa học được chủ đề khác từ tài liệu, nên chưa tạo bài luyện.',
  no_practice:
    'Ứng dụng chưa tạo được bài luyện đủ khác với bài trong tài liệu của con, nên chưa có bài luyện lần này.',
}

export function TopicFallback({
  reason,
  material,
  onChooseAnother,
  onHome,
}: {
  reason: FallbackReason
  /** The original on this device, when it is still open. */
  material: LocalMaterial | null
  onChooseAnother: () => void
  onHome: () => void
}): React.JSX.Element {
  return (
    <JourneyFrame step="Chủ đề" title="Chưa nhận ra chủ đề" onBack={null} onHome={onHome}>
      <p role="status" className="text-lg">
        {FALLBACK_TEXT[reason]}
      </p>
      <Note tone="info">
        Ứng dụng không đoán chủ đề và không tạo bài luyện không liên quan. Lần này không
        có kết quả học tập nào được ghi lại.
      </Note>
      {material !== null && (
        <details className={PANEL}>
          <summary className="cursor-pointer text-lg font-semibold">
            Xem tài liệu gốc
          </summary>
          <Original material={material} blocks={[]} highlightedId={null} />
        </details>
      )}
      <div className="flex flex-col gap-3 md:flex-row">
        <button type="button" className={PRIMARY} onClick={onChooseAnother}>
          Chọn tài liệu khác
        </button>
        <button type="button" className={SECONDARY} onClick={onHome}>
          Về trang chính
        </button>
      </div>
    </JourneyFrame>
  )
}
